import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ProjectService from "@/services/modules/project.service.ts";
import { CommonService } from "@/services";

interface EnvVar {
    key: string;
    value: string;
}

const EnvVariablesSettings = () => {
    const { projectId } = useParams();
    const [envVars, setEnvVars] = useState<EnvVar[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newKey, setNewKey] = useState("");
    const [newValue, setNewValue] = useState("");
    const [showValues, setShowValues] = useState<{ [key: string]: boolean }>({});


    const fetchEnvVars = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const response = await ProjectService.getProjectApiKeys(projectId);
            if (response.data?.keys) {
                const keysArray = Object.entries(response.data.keys).map(([key, value]) => ({
                    key,
                    value: String(value)
                }));
                setEnvVars(keysArray);
            }
        } catch (error) {
            console.error("Error fetching env vars:", error);
            toast.error("Failed to load environment variables");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) {
            fetchEnvVars();
        }
    }, [projectId, fetchEnvVars]);


    const handleAdd = () => {
        if (!newKey.trim()) {
            toast.error("Key cannot be empty");
            return;
        }

        if (envVars.some(e => e.key === newKey.trim())) {
            toast.error("Key already exists");
            return;
        }

        setEnvVars([...envVars, { key: newKey.trim(), value: newValue.trim() }]);
        setNewKey("");
        setNewValue("");
    };

    const handleDelete = (keyToDelete: string) => {
        setEnvVars(envVars.filter(e => e.key !== keyToDelete));
    };

    const handleSave = async () => {
        if (!projectId) return;
        setSaving(true);

        // Convert array back to object
        const payload = envVars.reduce((acc, curr) => {
            acc[curr.key] = curr.value;
            return acc;
        }, {} as Record<string, string>);

        try {
            await ProjectService.storeProjectApiKeys(projectId, { keys: payload });
            toast.success("Environment variables saved successfully");
            CommonService.handleMixpanelTrack('env_vars_saved', {
                project_id: projectId,
                count: envVars.length
            });
        } catch (error) {
            console.error("Error saving env vars:", error);
            toast.error("Failed to save environment variables");
        } finally {
            setSaving(false);
        }
    };

    const toggleShowValue = (key: string) => {
        setShowValues(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    return (
        <div className="settings-content">
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">Environment Variables</h3>
                    <p className="text-sm text-[var(--muted-foreground)]">
                        Manage API keys and environment variables for your project.
                    </p>
                </div>

                {/* Add New Variable */}
                <div className="flex flex-col gap-3">
                    <label className="text-sm font-medium text-[var(--foreground)]">Add New Variable</label>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="w-full md:flex-1">
                            <Input
                                placeholder="Key (e.g., API_KEY)"
                                value={newKey}
                                fullWidth={true}
                                onChange={(value) => setNewKey(value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                                className="font-mono bg-[var(--background)]"
                            />
                        </div>
                        <div className="w-full md:flex-1">
                            <Input
                                placeholder="Value"
                                value={newValue}
                                onChange={(value) => setNewValue(value)}
                                fullWidth={true}
                                type="password"
                                className="bg-[var(--background)]"
                            />
                        </div>
                        <Button onClick={handleAdd} disabled={!newKey.trim()}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add
                        </Button>
                    </div>
                </div>

                {/* List Variables */}
                <div className="grid grid-cols-1 gap-3">
                    {envVars.length === 0 ? (
                        <div className="text-center py-8 text-[var(--muted-foreground)] text-sm border border-dashed rounded-lg">
                            No environment variables defined
                        </div>
                    ) : (
                        envVars.map((env) => (
                            <div key={env.key} className="p-4 rounded-lg border border-[var(--border)] bg-[var(--card)] flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-[var(--muted-foreground)] mb-1 uppercase tracking-wider text-left">Key</div>
                                    <div className="font-mono text-sm font-medium text-[var(--foreground)] truncate select-all" title={env.key}>
                                        {env.key}
                                    </div>
                                </div>
                                <div className="flex-[2] min-w-0">
                                    <div className="text-xs font-medium text-[var(--muted-foreground)] mb-1 uppercase tracking-wider text-left">Value</div>
                                    <div className="flex items-center gap-2">
                                        <div className="font-mono text-sm text-[var(--foreground)] truncate flex-1 bg-[var(--background)] px-2 py-1 rounded border border-[var(--border)]">
                                            {showValues[env.key] ? env.value : "•".repeat(24)}
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 shrink-0 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                                            onClick={() => toggleShowValue(env.key)}
                                        >
                                            {showValues[env.key] ? (
                                                <EyeOff className="h-3.5 w-3.5" />
                                            ) : (
                                                <Eye className="h-3.5 w-3.5" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                <div className="flex items-center justify-end md:w-auto">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => handleDelete(env.key)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex justify-end pt-4 border-t border-[var(--border)]">
                    <Button onClick={handleSave} disabled={saving || loading}>
                        {saving ? (
                            <>Saving...</>
                        ) : (
                            <>
                                <Save className="w-4 h-4 mr-2" />
                                Save Changes
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EnvVariablesSettings;
